import * as React from 'react';
import { CSSProperties, FC, PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import { useControllableState } from '@shared/utils/hooks/useControllableState';
import { Box } from '@withjoy/joykit';
import { Indicator, IndicatorBar, TabbedPaneWrapper, TabsWrapper, TabText, TabWrapper, TopBar, TopBarWrapper } from './TabbedPane.styles';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';

interface TabProps {
  title?: string;
  isActive?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface ContentProps {}

interface ContentInternalProps
  extends ContentProps,
    Readonly<{
      selectedIndex: number;
    }> {}

interface TabbedPaneControlledProps
  extends TabbedPaneBaseProps,
    Readonly<{
      initialIndex?: never;
      selectedIndex: number;
      onChange: (newIndex: number) => void;
    }> {}

interface TabbedPaneUncontrolledProps
  extends TabbedPaneBaseProps,
    Readonly<{
      initialIndex: number;
      selectedIndex?: never;
      onChange?: never;
    }> {}

interface TabbedPaneBaseProps
  extends Readonly<{
    style?: CSSProperties;
  }> {}

const indicatorHoverExpandPx = 8;

const Tab: FC<TabProps> = ({ title, isActive, onClick, onMouseEnter, onMouseLeave }) => {
  return (
    <TabWrapper
      paddingTop={3}
      paddingX={[4, null, 7]}
      paddingBottom={'20px'}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <TabText
        variant="body1"
        component="span"
        isActive={isActive}
      >
        {title}
      </TabText>
    </TabWrapper>
  );
};

const Content: React.FC<ContentProps> = props => {
  const { selectedIndex, children } = props as PropsWithChildren<ContentInternalProps>;
  return (
    <Box>
      {React.Children.map(children, (child, index) => {
        const isActive = index === selectedIndex;
        return <div>{isActive ? child : null}</div>;
      })}
    </Box>
  );
};

interface ChildComponentsProps {
  Tab: typeof Tab;
  Content: typeof Content;
}

export type TabbedPaneProps = TabbedPaneControlledProps | TabbedPaneUncontrolledProps;

const TabbedPane: React.FC<TabbedPaneProps> & ChildComponentsProps = ({ initialIndex, selectedIndex, onChange, style, children }) => {
  const [currentIndex, setCurrentIndex] = useControllableState({ value: selectedIndex, defaultValue: initialIndex || 0, changeHandler: onChange });
  const tabsContainerRef = useRef<HTMLUListElement>(null);
  const topBarWrapperRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [indicatorLeft, setIndicatorLeft] = useState(0);
  const [indicatorWidth, setIndicatorWidth] = useState(0);
  const [topBarLeft, setTopBarLeft] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);
  const handleTabClickFunc = (originalOnClick: () => void, index: number) => {
    return () => {
      const isChanged = currentIndex !== index;
      if (isChanged) {
        if (originalOnClick) {
          originalOnClick();
        }
        setCurrentIndex(index);
      }
    };
  };
  const handleTabMouseEnterFunc = (index: number) => {
    return () => {
      const isChanged = hoverIndex !== index;
      if (isChanged) {
        setHoverIndex(index);
      }
    };
  };
  const handleTabMouseLeaveFunc = () => {
    return () => {
      setHoverIndex(null);
    };
  };

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 100);
    };
    setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize, false);
    return () => {
      window.removeEventListener('resize', onResize, false);
    };
  }, [setWindowWidth]);

  useIsomorphicLayoutEffect(() => {
    const tabsContainer = tabsContainerRef && tabsContainerRef.current;
    const topBarWrapper = topBarWrapperRef && topBarWrapperRef.current;
    const topBar = topBarRef && topBarRef.current;
    if (tabsContainer && topBarWrapper && topBar) {
      const containerRect = tabsContainer.getBoundingClientRect();
      const selectedTab = tabsContainer.children[currentIndex];

      if (selectedTab) {
        const selectedTabRect = selectedTab.children[0].getBoundingClientRect();
        setIndicatorLeft(selectedTabRect.left - containerRect.left);
        setIndicatorWidth(selectedTabRect.width);
        //
        const topBarWrapperRect = topBarWrapper.getBoundingClientRect();
        const topBarRect = topBar.getBoundingClientRect();
        if (topBar.scrollWidth > topBar.offsetWidth) {
          const topBarWrapperCenterX = topBarWrapperRect.width / 2;
          setTopBarLeft(topBarWrapperCenterX - selectedTabRect.width / 2 - (selectedTabRect.left - topBarRect.left));
        } else {
          setTopBarLeft(0);
        }
      }
    }
  }, [currentIndex, tabsContainerRef, topBarRef, topBarWrapperRef, windowWidth]);
  const isHoverLeft = hoverIndex !== null && hoverIndex < currentIndex;
  const isHoverRight = hoverIndex !== null && hoverIndex > currentIndex;
  return (
    <TabbedPaneWrapper style={style}>
      <TopBarWrapper ref={topBarWrapperRef}>
        <TopBar
          ref={topBarRef}
          style={{ left: topBarLeft }}
        >
          <TabsWrapper ref={tabsContainerRef}>
            {React.Children.map(children, (child, index) => {
              let el: ReactElement | null = null;
              if (React.isValidElement(child) && child.type === (<Tab />).type) {
                el = child as ReactElement<TabProps>;
                const tabProps = el.props;
                const tabOnClick = tabProps.onClick;
                el = React.cloneElement(el, {
                  isActive: index === currentIndex,
                  onMouseEnter: handleTabMouseEnterFunc(index),
                  onMouseLeave: handleTabMouseLeaveFunc(),
                  onClick: handleTabClickFunc(tabOnClick, index),
                });
              }
              return el;
            })}
            <IndicatorBar>
              <Indicator
                style={{
                  left: indicatorLeft - (isHoverLeft ? indicatorHoverExpandPx : 0),
                  width: indicatorWidth + (isHoverLeft || isHoverRight ? indicatorHoverExpandPx : 0),
                }}
              />
            </IndicatorBar>
          </TabsWrapper>
        </TopBar>
      </TopBarWrapper>
      <div>
        {React.Children.map(children, child => {
          let el: ReactElement | null = null;
          if (React.isValidElement(child) && child.type === (<Content />).type) {
            el = child as ReactElement<ContentInternalProps>;
            el = React.cloneElement(el, {
              selectedIndex: currentIndex,
            });
          }
          return el;
        })}
      </div>
    </TabbedPaneWrapper>
  );
};
TabbedPane.Tab = Tab;
TabbedPane.Content = Content;
TabbedPane.displayName = 'TabbedPane';
Tab.displayName = 'Tab';
Content.displayName = 'Content';

export { TabbedPane };
